// Verankert ein openModal-Fenster fest auf einer Höhe, statt es vertikal zu zentrieren.
//
// Die Einstellungen und der Artikel-Slot wachsen im VARIO-iframe auf volle Inhaltshöhe, VARIO
// scrollt außen. Ein zentriertes Modal landet dann in der Mitte des hohen iframes, und eine Box
// mit wechselnder Höhe (Suchergebnisse) springt bei jedem Tastendruck. Deshalb: fest verankern,
// mittig auf dem Element, das das Modal geöffnet hat (beim Öffnen hat es noch den Fokus). Den
// sichtbaren Ausschnitt kennt das iframe nicht, sicher sichtbar ist nur dieser Knopf. Die Box
// braucht dafür eine feste Höhe in px: `vh` meint im iframe die ganze Seitenhöhe, nicht den
// Bildschirm.
//
// Nachtrag 16.10.2026: Der sichtbare Ausschnitt ist doch messbar. Ein IntersectionObserver ohne
// eigenen root schneidet auch in einem fremden iframe gegen den Bildschirm des Hauptfensters.
// Nach dem Öffnen wird das Modal deshalb in den sichtbaren Teil gesetzt und auf dessen Höhe
// begrenzt. Vorher war es höher als das Fenster, man musste außen UND innen scrollen. Die Listen
// in den Dialogen schrumpfen dafür mit (flex), der Rest scrollt im Modal-Körper.

const MARGIN = 24;

/** Oberkante des Modals: mittig auf dem Anker, geklemmt auf [MARGIN, Höhe - Modal - MARGIN]. */
export function anchoredModalTop(anchorCenter: number | null, modalHeight: number, viewportHeight: number): number {
  const max = Math.max(MARGIN, viewportHeight - modalHeight - MARGIN);
  if (anchorCenter === null) return MARGIN;
  return Math.min(Math.max(Math.round(anchorCenter - modalHeight / 2), MARGIN), max);
}

/** Sichtbarer Teil des iframes in dessen eigenen Koordinaten. */
export interface VisibleBand {
  top: number;
  bottom: number;
}

/** Kleinste Modalhöhe; bei noch kleinerem Ausschnitt scrollt lieber außen das Fenster. */
const MIN_MODAL_HEIGHT = 240;

/** Oberkante und Höchsthöhe des Modals im sichtbaren Band: mittig auf dem Anker, ganz im Band. */
export function fitModalToBand(
  anchorCenter: number | null,
  modalHeight: number,
  band: VisibleBand,
): { top: number; maxHeight: number } {
  const maxHeight = Math.max(MIN_MODAL_HEIGHT, Math.round(band.bottom - band.top - 2 * MARGIN));
  const h = Math.min(modalHeight, maxHeight);
  const lo = Math.round(band.top + MARGIN);
  const hi = Math.max(lo, Math.round(band.bottom - h - MARGIN));
  const top = anchorCenter === null ? lo : Math.min(Math.max(Math.round(anchorCenter - h / 2), lo), hi);
  return { top, maxHeight };
}

/** Misst einmal, welcher Teil der Seite auf dem Bildschirm liegt. null = nicht messbar. */
function measureVisibleBand(timeoutMs = 400): Promise<VisibleBand | null> {
  if (typeof IntersectionObserver === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const probe = document.createElement('div');
    const height = Math.max(document.documentElement.scrollHeight, window.innerHeight);
    probe.style.cssText = `position:absolute;top:0;left:0;width:1px;height:${height}px;opacity:0;pointer-events:none;`;
    let done = false;
    const io = new IntersectionObserver((entries) => {
      const r = entries[0]?.intersectionRect;
      finish(r && r.height > 0 ? { top: r.top, bottom: r.bottom } : null);
    });
    function finish(band: VisibleBand | null) {
      if (done) return;
      done = true;
      io.disconnect();
      probe.remove();
      resolve(band);
    }
    document.body.appendChild(probe);
    io.observe(probe);
    window.setTimeout(() => finish(null), timeoutMs);
  });
}

/** Im onMount von openModal aufrufen, BEVOR ein Feld im Modal den Fokus bekommt. */
export function pinModalToOpener(root: HTMLElement): void {
  const opener = document.activeElement instanceof HTMLElement && document.activeElement !== document.body
    ? document.activeElement
    : null;
  const rect = opener?.getBoundingClientRect();
  const anchorCenter = rect ? rect.top + rect.height / 2 : null;
  const modal = root.querySelector<HTMLElement>('.kl-modal');
  root.style.alignItems = 'flex-start';
  if (!modal) return;
  modal.style.marginTop = `${anchoredModalTop(anchorCenter, modal.offsetHeight, window.innerHeight) - MARGIN}px`;
  // Danach in den sichtbaren Ausschnitt setzen. Erst die Höhe begrenzen, dann mit der neuen Höhe
  // platzieren. Das Wrap hat MARGIN Innenabstand, daher - MARGIN.
  void measureVisibleBand().then((band) => {
    if (!band || !modal.isConnected) return;
    modal.style.maxHeight = `${fitModalToBand(anchorCenter, modal.offsetHeight, band).maxHeight}px`;
    modal.style.marginTop = `${fitModalToBand(anchorCenter, modal.offsetHeight, band).top - MARGIN}px`;
  });
}
